/**
 * InputCelular: input restringido al canon chileno con prefijo visual `+56 9`.
 *
 * Sprint 14 S14-Cel.3. La doctrina del celular (S14-Cel) almacena solo 9
 * dígitos chilenos empezando con `9` (`912345678`). El `+56` es prefijo
 * visual fijo de país; el `9` es prefijo de móvil chileno fijo. Lo único
 * que varía son los 8 dígitos restantes — eso es lo que el vecino tipea.
 *
 * Patrón visual:
 *   ┌─────────┬──────────────────────┐
 *   │ +56 9   │ 1234 5678            │
 *   └─────────┴──────────────────────┘
 *      fijo      input (8 dígitos)
 *
 * API:
 *
 *   <InputCelular
 *     value={celular}                  // "912345678" canónico o ""
 *     onChange={(v) => setCelular(v)}  // emite "912345678" o ""
 *     id="celular"
 *   />
 *
 * Comportamiento:
 * - El input acepta SOLO dígitos (otros caracteres se ignoran).
 * - Máximo 8 caracteres en el input (los 8 dígitos post-9).
 * - Si el `value` entrante calza con `^9\d{8}$`, el input muestra los 8
 *   últimos dígitos. Si no, muestra vacío (defensivo — input legacy
 *   inválido se descarta visualmente, el vecino lo retipea).
 * - `onChange` siempre emite `""` (input vacío) o `"9" + N dígitos`
 *   tipeados. La validación de completitud queda en el parent
 *   (`value === "" || /^9\d{8}$/.test(value)`).
 */
import { type InputHTMLAttributes } from 'react';

interface InputCelularProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'maxLength' | 'inputMode'> {
  /** Valor canónico: "" o "9XXXXXXXX". */
  value: string;
  /** Emite "" o "9" + dígitos tipeados (parcial o completo). */
  onChange: (value: string) => void;
}

// Acepta el canónico completo (`9` + 8 dígitos) Y los parciales que el
// parent va guardando mientras el vecino tipea (`9` + 0..8 dígitos). Si solo
// se aceptara el completo, cada tecla dejaría el value en "9X" (parcial) →
// no calzaría → el input se vaciaría en cada pulsación (bug de entrada).
const ENTRADA_VALIDA = /^9\d{0,8}$/;

export function InputCelular({
  value,
  onChange,
  className = '',
  id,
  ...rest
}: InputCelularProps) {
  // Strip "9" inicial si el valor calza (completo o parcial). Cualquier valor
  // entrante mal-formado (ej. dato legacy "+56 9 1234 5678" que no se
  // alcanzó a migrar) cae a "" — el usuario lo retipea.
  const digits = ENTRADA_VALIDA.test(value) ? value.slice(1) : '';

  // Display con espacio al medio "1234 5678" (estándar chileno de presentación).
  // El input acepta el espacio al tipear pero lo strip al emitir.
  const displayed =
    digits.length <= 4 ? digits : `${digits.slice(0, 4)} ${digits.slice(4)}`;

  function handleInput(raw: string) {
    const onlyDigits = raw.replace(/\D/g, '').slice(0, 8);
    onChange(onlyDigits.length === 0 ? '' : `9${onlyDigits}`);
  }

  return (
    <div
      className={`flex items-stretch rounded-sm bg-bg-sunken border-[0.5px] border-white/15 overflow-hidden focus-within:border-orange focus-within:ring-1 focus-within:ring-orange/30 ${className}`.trim()}
    >
      <span
        className="flex items-center px-3 py-2 bg-bg-page/40 border-r-[0.5px] border-white/[0.08] text-text-muted text-sm select-none whitespace-nowrap"
        aria-hidden="true"
      >
        +56 9
      </span>
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        // maxLength=9 = 8 dígitos + 1 espacio visual al medio.
        maxLength={9}
        value={displayed}
        onChange={(e) => handleInput(e.target.value)}
        placeholder="1234 5678"
        className="flex-1 min-w-0 bg-transparent text-text-primary px-3 py-2 outline-none placeholder:text-text-muted"
        aria-label="Celular chileno (8 dígitos después del prefijo +56 9)"
        {...rest}
      />
    </div>
  );
}
